<script setup lang="ts">
import { setupOfferEnd, supportedImportTools, supportMappedTools } from '~/data/setup-offer';

const props = withDefaults(defineProps<{ competitorSlug?: string; location?: string }>(), { location: 'pricing' });
const { tr } = useHomeI18n();
const { copyLocaleCode } = useWorkspaceLocale();
const { addReferralToUrl } = useReferral();
const { offer, active } = useSetupOffer();
const importTool = computed(() => supportedImportTools[props.competitorSlug || '']);
const supportMappedTool = computed(() => supportMappedTools[props.competitorSlug || '']);
const endLabel = computed(() => new Intl.DateTimeFormat(copyLocaleCode.value, { year: 'numeric', month: 'long', day: 'numeric' }).format(setupOfferEnd(offer.value.endsAt)));
</script>

<template>
  <section v-if="active" class="switch-save" data-switch-save>
    <div class="switch-save-copy">
      <h2>{{ tr('Free account setup & migration help') }}</h2>
      <p>{{ tr('Our team sets up your workspace with you and helps move your clients and invoices from the tools you use now.') }}</p>
      <p v-if="importTool" class="switch-save-import">
        <template v-if="importTool === 'GoHighLevel'">{{ tr('Import your GoHighLevel CSV export with the AI importer. Review the column mapping and the AI usage estimate before you start.') }}</template>
        <template v-else-if="importTool === 'Jobber'">{{ tr('Import your Jobber CSV export with the AI importer. Review the column mapping and the AI usage estimate before you start.') }}</template>
      </p>
      <p v-else-if="supportMappedTool === 'HoneyBook'" class="switch-save-import">{{ tr('There’s no direct import from HoneyBook. Export your clients as a spreadsheet and bring them in with CSV import, and Workspace369 support can help you map it.') }}</p>
      <p v-else class="switch-save-import">{{ tr('Export your data as a spreadsheet and AI Import maps the columns for you. Support can help with anything unusual.') }}</p>
      <p class="switch-save-date">{{ tr('Offer ends') }} <time :datetime="offer.endsAt">{{ endLabel }}</time>.</p>
    </div>
    <div class="switch-save-action">
      <a
        :href="addReferralToUrl('https://app.workspace369.com/signup')"
        class="btn-sm text-white bg-indigo-600 hover:bg-indigo-700"
        data-conversion-event="start_free_click"
        data-conversion-label="Switch & Save signup"
        :data-conversion-location="`${location}-switch-save`"
      >{{ tr('Start free trial') }} <span aria-hidden="true">→</span></a>
      <TrialDisclosure compact />
      <p>{{ tr('After signup, claim free setup in your workspace.') }}</p>
    </div>
  </section>
</template>

<style scoped>
.switch-save { display: flex; align-items: center; gap: 2rem; max-width: 1120px; margin: 3rem auto; padding: 2rem; border: 1px solid #c7d2fe; border-radius: 10px; background: #eef2ff; color: #172554; }
.switch-save-copy { flex: 1; min-width: 0; }
h2 { margin: 0 0 .8rem; font-size: clamp(1.4rem, 3vw, 1.8rem); font-weight: 700; line-height: 1.25; }
.switch-save-copy > p { max-width: 70ch; font-size: .95rem; line-height: 1.6; }
.switch-save-import { margin-top: .6rem; }
.switch-save-copy > .switch-save-date { margin-top: .85rem; font-size: .8rem; font-weight: 600; }
.switch-save-action { flex: 0 0 230px; }
.switch-save-action a { display: inline-flex; gap: .65rem; align-items: center; justify-content: center; padding: .85rem 1.2rem; border-radius: 8px; font-weight: 600; }
.switch-save-action a:focus-visible { outline: 3px solid #312e81; outline-offset: 4px; }
.switch-save-action p { margin-top: .65rem; font-size: .8rem; line-height: 1.5; }
@media (max-width: 760px) { .switch-save { margin: 2rem 1rem; padding: 1.5rem; flex-direction: column; align-items: stretch; gap: 1.25rem; } .switch-save-action { flex-basis: auto; } }
:global(.dark .switch-save) { background: #17203c; border-color: #414e73; color: #eef2ff; }
</style>
